import { useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import YupPassword from 'yup-password'
YupPassword(yup)

import {
  REGISTER_FINANCES_USER_KEY,
  emailCapture,
  registerFinancesUser,
} from '@/actions/authActions'
import { getFetchError } from '@/reducers/fetch'
import {
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsSetUserId, useAnalyticsView } from '@/features/amplitude'
import { getFieldName } from '@/base'
import { TypographyStyles } from '@/styles'
import { Text, Alert, Link, Form, Button, Image } from '@/ui'
import { useScrollToFieldError } from '@/utils/formHelpers'
import { COMPLIANCE_PATHS } from '@/features/compliance/lib/constants'

import { ReadinessPreviewPanel } from './ReadinessPreviewPanel'
import {
  buildSignupPayload,
  sharedSignupFieldRules,
  SignupFlow,
  type PracticeReadinessAccountFormValues,
} from '../../signupForm.contract'

/**
 * Customers arrive cold from a marketing link, so there is no email-capture step
 * to read session storage from and nothing in-app to navigate back to.
 *
 * Layout matches the 2.0 Core App Conversion design (Figma node 1076:4292) rather than
 * the shared `SignupPageLayout`, deliberately: that design has no header, logout, or
 * progress bar, unlike every other screen in this flow.
 */
export const PracticeReadinessAccount = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigateWithPersistParams()
  const pageView = useAnalyticsView()
  const setAnalyticsUserId = useAnalyticsSetUserId()
  const { scrollRef, scrollToRef } = useScrollRef()
  const signupError = useReselector(getFetchError, REGISTER_FINANCES_USER_KEY)
  const [searchParams] = useSearchParams()

  const validationSchema = useMemo(
    () => yup.object({ ...sharedSignupFieldRules }),
    []
  )

  useEffect(() => {
    pageView('sign up practice readiness account')
  }, [pageView])

  const capturedEmails = useRef(new Set<string>())

  /**
   * Posts the email to lead capture so the HubSpot form gets a chance to create the
   * contact ahead of the signup request's CRM write, which is what lets HubSpot stamp
   * Original Source from the visitor's pageview history.
   */
  const captureEmail = async (email: string): Promise<void> => {
    const normalized = email.toLowerCase().trim()
    if (!normalized || capturedEmails.current.has(normalized)) {
      return
    }
    capturedEmails.current.add(normalized)
    try {
      await emailCapture({ email: normalized })(dispatch)
    } catch {
      // Attribution is a side-effect; never let it fail the signup.
    }
  }

  const formik = useFormik<PracticeReadinessAccountFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      termsOfService: false,
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: async (values) => {
      await captureEmail(values.email)

      const res = await registerFinancesUser(
        buildSignupPayload({
          values,
          searchParams,
          signupFlow: SignupFlow.practiceReadiness,
        })
      )(dispatch)

      if (res) {
        setAnalyticsUserId(res.user.uuid)
        // Registration dispatches updateCurrentUser, which flips
        // security.isAuthenticated true (securityReducer extraReducer), so the
        // private compliance intake is reachable straight from the signup step.
        navigate(COMPLIANCE_PATHS.intake)
      } else {
        scrollToRef()
      }
    },
  })

  useScrollToFieldError(formik)

  const { submitForm, isSubmitting } = formik

  return (
    // Column below `lg`, row above: the preview panel is the proof of what the
    // account is for, so on a phone it follows the form rather than being
    // dropped — the same content, read in sequence instead of side by side.
    //
    // `lg` and not `md`: the form column is `min-w-[580px]` from the moment
    // the row starts, which leaves the preview 92px at 768px. Two columns only
    // have room from about 1000px.
    <div className="flex min-h-screen w-full flex-col lg:flex-row">
      <div className="relative flex w-full shrink-0 flex-col items-center gap-8 bg-neutral-0 px-8 pb-16 lg:w-[42%] lg:min-w-[580px] lg:px-[81px]">
        {/* Bleeds past the panel's own right edge on purpose. Above `lg` the yellow panel
            is a later sibling with its own opaque background, so it paints over the
            overflow, matching the Figma design's negative-margin piggy bank. Below `lg`
            that panel sits underneath rather than beside, so nothing covers the bleed and
            it would push the page 40px wider than the viewport.

            The clip is on this overlay rather than on the form column, so it contains the
            one bleeding image and nothing else: clipping the column would cut any
            non-portaled tooltip or focus ring near its edge, and would kill `position:
            sticky` for every descendant. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden lg:overflow-visible"
        >
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/piggy_bank_pink.svg"
            alt=""
            width={160}
            className="absolute -right-10 top-4 -rotate-[15deg]"
          />
        </div>

        <Image
          src="https://heard-images.s3.us-west-1.amazonaws.com/heard_logo_green_2x.png"
          alt="heard logo"
          width={76}
          className="self-start py-10"
        />

        <div className="flex w-full flex-1 flex-col items-center justify-center">
          <div className="flex w-full max-w-[418px] flex-col gap-10">
            <div className="flex flex-col gap-1">
              <Text variant={TypographyStyles.DISPLAY_MD}>
                Create a free account
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                Access your personalized report any time with a free account.
              </Text>
            </div>

            {signupError && (
              <div className="w-full">
                <span ref={scrollRef} />
                <Alert variant="error" description={signupError.message} />
              </div>
            )}

            <FormikProvider value={formik}>
              <div className="flex w-full flex-col gap-8">
                <div className="flex flex-col gap-4">
                  <Form.Input
                    placeholder="First name"
                    aria-label="First Name"
                    name={getFieldName<PracticeReadinessAccountFormValues>(
                      'firstName'
                    )}
                  />

                  <Form.Input
                    placeholder="Last name"
                    aria-label="Last Name"
                    name={getFieldName<PracticeReadinessAccountFormValues>(
                      'lastName'
                    )}
                  />

                  <Form.Input
                    placeholder="Business email address"
                    aria-label="Business Email Address"
                    name={getFieldName<PracticeReadinessAccountFormValues>(
                      'email'
                    )}
                  />

                  <div className="flex flex-col gap-4">
                    <Form.Input
                      placeholder="Password"
                      aria-label="Password"
                      name={getFieldName<PracticeReadinessAccountFormValues>(
                        'password'
                      )}
                      type="password"
                      autoComplete="new-password"
                    />

                    <Form.Input
                      placeholder="Confirm password"
                      aria-label="Confirm Password"
                      name={getFieldName<PracticeReadinessAccountFormValues>(
                        'confirmPassword'
                      )}
                      type="password"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <Form.Checkbox
                  name={getFieldName<PracticeReadinessAccountFormValues>(
                    'termsOfService'
                  )}
                  className="[&_label]:text-sm"
                  label={
                    <span>
                      By continuing, you&apos;re creating a Heard account and
                      agree to the{' '}
                      <Link
                        newPage
                        href="https://www.joinheard.com/terms-of-service"
                      >
                        Terms of Service
                      </Link>
                      ,{' '}
                      <Link newPage href="https://www.joinheard.com/privacy">
                        Privacy Policy
                      </Link>
                      , and{' '}
                      <Link
                        newPage
                        href="https://www.joinheard.com/baa-agreement"
                      >
                        Business Associate Agreement
                      </Link>
                      .
                    </span>
                  }
                />

                <div className="flex flex-col gap-2">
                  <Button
                    className="w-full"
                    onClick={submitForm}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    id="btn-practice-readiness-create-account"
                  >
                    Get Started
                  </Button>

                  <Text
                    variant={TypographyStyles.BODY_XS}
                    className="text-center"
                  >
                    Already have an account?{' '}
                    <Link
                      href="/login"
                      className="text-xs font-medium text-neutral-900"
                    >
                      Log in
                    </Link>
                  </Text>
                </div>
              </div>
            </FormikProvider>
          </div>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-neutral-0 bg-gradient-to-b from-blue-50/35 to-moss-25/0 px-6 py-12 lg:px-12 lg:py-16">
        <ReadinessPreviewPanel />
      </div>
    </div>
  )
}
